import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { InfoTooltip } from '@morpho-org/ui-v6/client';
import { cn, formatters } from '@morpho-org/ui-v6/utils';
import { TooltipLink } from '@repo/ui';
import { WarningTriangle } from '@repo/ui/icons';
import { FORCED_DEALLOCATION_HELP_URL, IN_KIND_REDEMPTION_HELP_URL } from '@/config/external-urls';
import { formatFeeAmount, formatTokenAmount } from '@/helpers/formatters';

/**
 * What the fee is charged on. Carried as one object because the sentence needs every
 * part of it: a caller that cannot state the whole split honestly passes nothing and
 * gets the short explanation instead.
 */
export interface ForceDeallocationContext {
  /** What the Vault can pay out without touching a Market. */
  availableLiquidity: bigint;
  /** What the user asked to withdraw. */
  withdrawalAmount: bigint;
  /** What the plan pulls back from Markets to cover the rest. */
  deallocatedAssets: bigint;
  /** The Vault's force-deallocate penalty (WAD). */
  feeRate: bigint;
}

/**
 * What an in-kind redemption's fee is charged on. Deliberately not folded into
 * `ForceDeallocationContext`: the two paths charge on different bases, and the numbers below are
 * carried rather than re-derived so the sentence reconciles exactly (`netAssets + fee ===
 * inKindAssets`, and `fee === feeRate x netAssets`).
 */
export interface InKindRedemptionFeeContext {
  /** Idle, paid to the wallet first and fee-free. "Available liquidity" in Cole's vocabulary. */
  availableLiquidity: bigint;
  /** What the user asked to exit. */
  withdrawalAmount: bigint;
  /** The part that cannot be paid in cash and becomes a Market position, fee included. */
  inKindAssets: bigint;
  /** What is actually supplied to the Market, i.e. `inKindAssets` less the fee. */
  netAssets: bigint;
  /** The adapter's penalty rate (WAD). */
  feeRate: bigint;
}
interface ForceDeallocationFeeExplanationProps {
  vaultAsset: InterfaceToken | null | undefined;
  feeAssets: bigint;
  forceDeallocation?: ForceDeallocationContext;
  inKindRedemption?: InKindRedemptionFeeContext;
}

/**
 * Why the withdrawal costs anything, as tooltip content. Shared by the Fee row and by the
 * recap's net-received card, so the two never explain the same fee differently.
 */
export const ForceDeallocationFeeExplanation = t0 => {
  const $ = _c(23);
  const {
    vaultAsset,
    feeAssets,
    forceDeallocation,
    inKindRedemption
  } = t0;
  let t1;
  if ($[0] !== vaultAsset) {
    t1 = amount => formatTokenAmount(vaultAsset, amount);
    $[0] = vaultAsset;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const format = t1;
  let t2;
  if ($[2] !== feeAssets || $[3] !== forceDeallocation || $[4] !== format || $[5] !== vaultAsset?.decimals || $[6] !== vaultAsset?.price || $[7] !== vaultAsset?.symbol) {
    t2 = !!forceDeallocation && <p>This Vault holds {format(forceDeallocation.availableLiquidity)} in available liquidity and can be withdrawn with no fee. Withdrawing {format(forceDeallocation.withdrawalAmount)}{" "}pulls the other {format(forceDeallocation.deallocatedAssets)} back from Markets early — a force deallocation, which this Vault charges{" "}{formatters.percent.digits(2).unit("%").of(forceDeallocation.feeRate, 18)} on:{" "}{formatFeeAmount({
        price: vaultAsset?.price,
        amount: feeAssets,
        decimals: vaultAsset?.decimals,
        withTrailingZero: false,
        symbol: vaultAsset?.symbol
      })}.</p>;
    $[2] = feeAssets;
    $[3] = forceDeallocation;
    $[4] = format;
    $[5] = vaultAsset?.decimals;
    $[6] = vaultAsset?.price;
    $[7] = vaultAsset?.symbol;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== feeAssets || $[10] !== format || $[11] !== inKindRedemption || $[12] !== vaultAsset?.decimals || $[13] !== vaultAsset?.price || $[14] !== vaultAsset?.symbol) {
    t3 = !!inKindRedemption && <p>This Vault holds {format(inKindRedemption.availableLiquidity)} in available liquidity, which can be withdrawn with no fee. Withdrawing{" "}{format(inKindRedemption.withdrawalAmount)} supplies the other{" "}{format(inKindRedemption.inKindAssets)} to a Market you choose as a lending position:{" "}{format(inKindRedemption.netAssets)} after a{" "}{formatters.percent.digits(2).unit("%").of(inKindRedemption.feeRate, 18)} fee of{" "}{formatFeeAmount({
        price: vaultAsset?.price,
        amount: feeAssets,
        decimals: vaultAsset?.decimals,
        withTrailingZero: false,
        symbol: vaultAsset?.symbol
      })}.</p>;
    $[9] = feeAssets;
    $[10] = format;
    $[11] = inKindRedemption;
    $[12] = vaultAsset?.decimals;
    $[13] = vaultAsset?.price;
    $[14] = vaultAsset?.symbol;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p>The fee stays in the Vault, shared across the depositors who remain.</p>;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== inKindRedemption) {
    t5 = inKindRedemption ? <TooltipLink href={IN_KIND_REDEMPTION_HELP_URL} target="_blank" rel="noopener noreferrer">Learn about in-kind redemption →</TooltipLink> : <TooltipLink href={FORCED_DEALLOCATION_HELP_URL} target="_blank" rel="noopener noreferrer">What is force deallocation?</TooltipLink>;
    $[17] = inKindRedemption;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t5) {
    t6 = <>{t2}{t3}{t4}{t5}</>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
interface VaultV2FeeRowProps {
  vaultAsset: InterfaceToken | null | undefined;
  feeAssets: bigint;
  className?: string;
  /** Matches the surrounding rows: the summary cards run at `text-3xs`, the recap at `text-xs`. */
  textClassName?: string;
  forceDeallocation?: ForceDeallocationContext;
  inKindRedemption?: InKindRedemptionFeeContext;
}

/**
 * The force-deallocate fee, shown on the vault action panel and the position manager. The asset
 * symbol sits in the label, so the value is a bare amount — the same shape as the deposit row
 * above it. The transaction recap states the same fee as a net-received card instead.
 */
const VaultV2FeeRow = t0 => {
  const $ = _c(29);
  const {
    vaultAsset,
    feeAssets,
    className,
    textClassName: t1,
    forceDeallocation,
    inKindRedemption
  } = t0;
  const textClassName = t1 === undefined ? "text-3xs" : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex flex-row items-center justify-between", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== textClassName) {
    t3 = cn("text-warning-foreground", textClassName);
    $[2] = textClassName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = vaultAsset?.symbol ? `Fee (${vaultAsset.symbol})` : "Fee";
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <WarningTriangle />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== feeAssets || $[9] !== forceDeallocation || $[10] !== inKindRedemption || $[11] !== vaultAsset) {
    t7 = <InfoTooltip icon={t6} className="text-warning-foreground" tooltipContent={<ForceDeallocationFeeExplanation vaultAsset={vaultAsset} feeAssets={feeAssets} forceDeallocation={forceDeallocation} inKindRedemption={inKindRedemption} />} />;
    $[8] = feeAssets;
    $[9] = forceDeallocation;
    $[10] = inKindRedemption;
    $[11] = vaultAsset;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <div className="gap-2xs flex flex-row items-center">{t5}{t7}</div>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== textClassName) {
    t9 = cn("text-warning-foreground", textClassName);
    $[16] = textClassName;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = vaultAsset?.price;
  const t11 = vaultAsset?.decimals;
  let t12;
  if ($[18] !== feeAssets || $[19] !== t10 || $[20] !== t11) {
    t12 = formatFeeAmount({
      price: t10,
      amount: feeAssets,
      decimals: t11,
      withTrailingZero: false
    });
    $[18] = feeAssets;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t9) {
    t13 = <span className={t9}>{t12}</span>;
    $[22] = t12;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t2 || $[27] !== t8) {
    t14 = <div className={t2} data-testid="vault-v2-fee-row">{t8}{t13}</div>;
    $[25] = t13;
    $[26] = t2;
    $[27] = t8;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
};
export default VaultV2FeeRow;